<template>
  <div class="social-network-input-group">
    <label class="block my-3">
      {{ t('templateBuilder.fields.elementsConfiguration', null) }}
    </label>
    <div class="elements-configuration">
      <draggable
        v-model="socialNetworks"
        item-key="key"
        handle=".drag-icon"
        @change="handleOrderChange"
      >
        <template #item="{ element, index }">
          <div class="social-network-item mb-4">
            <div class="social-network-header">
              <i
                class="fas fa-arrows-alt-v mr-2 drag-icon"
                :title="t('templateBuilder.fields.dragToReorder')"
              />
              <span class="network-label">{{ getNetworkLabel(element) }}</span>
              <Button
                icon="far fa-trash"
                class="p-button-text p-button-danger delete-button"
                :title="t('templateBuilder.fields.delete')"
                @click="handleRemoveNetwork(index)"
              />
            </div>
            <div class="social-network-content">
              <div class="network-selector">
                <Dropdown
                  v-model="element.network"
                  :options="availableNetworks"
                  option-label="label"
                  option-value="value"
                  :placeholder="t('templateBuilder.fields.selectNetwork')"
                  class="w-full"
                  @change="handleNetworkTypeChange(index, $event)"
                >
                  <template #value="slotProps">
                    <div
                      v-if="slotProps.value"
                      class="network-option"
                      style="display: flex; align-items: center; gap: 8px;"
                    >
                      <i
                        v-if="slotProps.value === 'separator'"
                        class="far fa-grip-lines"
                        style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; color: #9ca3af; flex-shrink: 0;"
                      />
                      <img
                        v-else-if="slotProps.value !== 'custom'"
                        :key="`${slotProps.value}-${iconStyleKey}`"
                        :src="getNetworkIconUrl(slotProps.value)"
                        :alt="slotProps.value"
                        class="network-icon"
                        style="width: 24px; height: 24px; flex-shrink: 0; border-radius: 4px;"
                      >
                      <i
                        v-else
                        class="far fa-image"
                        style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; color: #9ca3af; flex-shrink: 0;"
                      />
                      <span>{{ getNetworkDisplayLabel(slotProps.value) }}</span>
                    </div>
                    <span v-else>{{ t('templateBuilder.fields.selectNetwork') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div class="network-option" style="display: flex; align-items: center; padding: 4px 0;">
                      <i
                        v-if="slotProps.option.value === 'separator'"
                        class="far fa-grip-lines"
                        style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; color: #9ca3af; flex-shrink: 0;"
                      />
                      <img
                        v-else-if="slotProps.option.value !== 'custom'"
                        :key="`${slotProps.option.value}-${iconStyleKey}`"
                        :src="getNetworkIconUrl(slotProps.option.value)"
                        :alt="slotProps.option.label"
                        class="network-icon"
                        style="width: 24px; height: 24px; flex-shrink: 0; border-radius: 4px;"
                      >
                      <i
                        v-else
                        class="far fa-image"
                        style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; color: #9ca3af; flex-shrink: 0;"
                      />
                      <span style="line-height: 24px; margin-left: 8px;">{{ slotProps.option.label }}</span>
                    </div>
                  </template>
                </Dropdown>
              </div>
              <!-- Champs pour les réseaux sociaux -->
              <template v-if="element.network !== 'separator'">
                <div class="url-field">
                  <label class="block mb-2">
                    {{ t('templateBuilder.fields.linkUrl') }}
                  </label>
                  <InputText
                    v-model="element.url"
                    class="w-full"
                    :placeholder="t('templateBuilder.fields.enterUrl')"
                    @input="handleUrlChange(index)"
                  />
                </div>
                <div
                  v-if="element.network === 'custom'"
                  class="custom-icon-section mt-3"
                >
                <label class="block mb-2">
                  {{ t('templateBuilder.fields.uploadCustomIcon') }}
                </label>
                <div
                  v-if="element.customIcon"
                  class="custom-icon-preview"
                >
                  <img
                    :src="element.customIcon"
                    alt="Custom icon"
                    class="preview-image"
                  >
                  <div class="preview-actions">
                    <Button
                      icon="far fa-exchange"
                      class="p-button-text"
                      :title="t('templateBuilder.fields.changeImage')"
                      @click="openFileManager(index)"
                    />
                    <Button
                      icon="far fa-trash"
                      class="p-button-text p-button-danger"
                      :title="t('templateBuilder.fields.removeImage')"
                      @click="handleRemoveCustomIcon(index)"
                    />
                  </div>
                </div>
                <div
                  v-else
                  class="custom-icon-upload"
                  :class="{ 'drag-over': dragOverIndex === index }"
                  @click="openFileManager(index)"
                  @dragover.prevent="onDragOver($event, index)"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="onDrop($event, index)"
                >
                  <div class="upload-zone">
                    <ProgressSpinner
                      v-if="uploadingIndex === index"
                      style="width: 40px; height: 40px;"
                      stroke-width="4"
                    />
                    <template v-else>
                      <i class="far fa-cloud-upload upload-icon" />
                      <p class="upload-text">
                        {{ t('templateBuilder.fields.dragOrSelectImage') }}
                      </p>
                      <p class="upload-hint">
                        {{ t('templateBuilder.fields.acceptedFormatsEmail') }}
                      </p>
                      <Button
                        :label="t('templateBuilder.fields.selectImage')"
                        class="mt-2"
                        outlined
                      />
                    </template>
                  </div>
                </div>
              </div>
              </template>
              <!-- Champs pour le séparateur -->
              <template v-else>
                <div class="separator-fields">
                  <div class="separator-field">
                    <label class="block mb-2">
                      {{ t('templateBuilder.fields.separatorPadding') }}
                    </label>
                    <BaseInputNumber
                      v-model="element.separatorPaddingValue"
                      suffix="px"
                      :min="0"
                      :max="100"
                      :step="1"
                      class="w-full"
                      @update:model-value="handleSeparatorPaddingInput(index, $event)"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </draggable>
      <div class="add-network-section">
        <Button
          :label="t('templateBuilder.fields.addElement')"
          icon="far fa-plus"
          class="p-button-outlined w-full"
          @click="handleAddNetwork"
        />
      </div>
    </div>
    <FileManager
      v-if="displayFileManager"
      :display-dialog="displayFileManager"
      extensions="image/*"
      @on-close-dialog="displayFileManager = false"
      @on-choose-file="onChooseCustomIcon"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref, computed, watch,
} from 'vue';
import draggable from 'vuedraggable';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import ProgressSpinner from 'primevue/progressspinner';
import FileManager from '@/components/file-manager/FileManager.vue';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
  File,
} from '@/types';
import { useI18n } from 'vue-i18n';
import { UserState } from '@/composables/User';
import { createNewFileInMedia } from '@/composables/configs/configuration';
import { getSocialNetworkIconStyleV4 } from '@/components/template-builder/getters';

interface SocialNetworkItem {
  key: string;
  network: string;
  url: string;
  customIcon?: string;
  // Propriétés spécifiques au séparateur (espacement uniquement)
  separatorPadding?: string;
  separatorPaddingValue?: number;
}

const NETWORK_LIST = [
  { label: 'Separator', value: 'separator', translationKey: 'templateBuilder.fields.separator', isSeparator: true },
  { label: 'Custom', value: 'custom', translationKey: 'templateBuilder.fields.custom' },
  { label: 'Facebook', value: 'facebook', translationKey: 'templateBuilder.fields.facebook' },
  { label: 'X', value: 'twitter', translationKey: 'templateBuilder.fields.twitter' },
  { label: 'YouTube', value: 'youtube', translationKey: 'templateBuilder.fields.youtube' },
  { label: 'Pinterest', value: 'pinterest', translationKey: 'templateBuilder.fields.pinterest' },
  { label: 'Tumblr', value: 'tumblr', translationKey: 'templateBuilder.fields.tumblr' },
  { label: 'Instagram', value: 'instagram', translationKey: 'templateBuilder.fields.instagram' },
  { label: 'WhatsApp', value: 'whatsapp', translationKey: 'templateBuilder.fields.whatsapp' },
  { label: 'Snapchat', value: 'snapchat', translationKey: 'templateBuilder.fields.snapchat' },
  { label: 'TikTok', value: 'tiktok', translationKey: 'templateBuilder.fields.tiktok' },
  { label: 'LinkedIn', value: 'linkedin', translationKey: 'templateBuilder.fields.linkedin' },
  { label: 'Telegram', value: 'telegram', translationKey: 'templateBuilder.fields.telegram' },
  { label: 'Messenger', value: 'messenger', translationKey: 'templateBuilder.fields.messenger' },
  { label: 'Discord', value: 'discord', translationKey: 'templateBuilder.fields.discord' },
  { label: 'Reddit', value: 'reddit', translationKey: 'templateBuilder.fields.reddit' },
  { label: 'Mail', value: 'email', translationKey: 'templateBuilder.fields.email' },
];

export default defineComponent({
  name: 'SocialNetworkInputGroup',

  components: {
    draggable,
    InputText,
    Button,
    Dropdown,
    ProgressSpinner,
    FileManager,
    BaseInputNumber,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },
    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const displayFileManager = ref(false);
    const currentEditIndex = ref<number>(-1);
    const dragOverIndex = ref<number>(-1);
    const iconStyleKey = ref(0); // Clé pour forcer le rafraîchissement des icônes

    // Récupérer le style d'icône actuel depuis le template
    const getIconStyleFromTemplate = (): string => {
      const imgSelector = `${props.parserValues.selector} img`;
      const style = getSocialNetworkIconStyleV4(imgSelector);
      return style || '1';
    };
    const iconStyle = ref(getIconStyleFromTemplate());
    const uploadingIndex = ref<number>(-1);
    const idShop: number = UserState.activeShop ? UserState.activeShop.id : 0;

    const socialNetworks = ref<SocialNetworkItem[]>([]);

    const initializeSocialNetworks = () => {
      const items = property.value.value || [];
      socialNetworks.value = items.map((item: any) => ({
        key: item.key || `spm_${item.network || 'custom'}_${Date.now()}`,
        network: item.network || extractNetworkFromKey(item.key),
        url: item.value || item.url || '',
        customIcon: item.customIcon || '',
        // Propriétés du séparateur (espacement uniquement)
        separatorPadding: item.separatorPadding || '5px',
        separatorPaddingValue: parseInt((item.separatorPadding || '5px').replace(/\D/g, ''), 10) || 5,
      }));
    };

    const extractNetworkFromKey = (key: string): string => {
      if (!key) return 'custom';
      const match = key.match(/spm_(\w+)/);
      return match ? match[1] : 'custom';
    };

    const availableNetworks = computed(() => NETWORK_LIST.map((network) => ({
      ...network,
      label: t(network.translationKey),
    })));

    const getNetworkLabel = (element: SocialNetworkItem): string => {
      const network = NETWORK_LIST.find((n) => n.value === element.network);
      return network ? t(network.translationKey) : t('templateBuilder.fields.custom');
    };

    const getNetworkDisplayLabel = (networkValue: string): string => {
      const network = NETWORK_LIST.find((n) => n.value === networkValue);
      return network ? t(network.translationKey) : networkValue;
    };

    const getNetworkIconUrl = (network: string): string => {
      if (network === 'custom') return '';
      // Forcer le style 2 pour les dropdowns (icônes carrées avec fond)
      return `https://media.shopimind.io/img/icons/socialv2/2/${network}.png`;
    };

    const generateUniqueKey = (network: string): string => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      return `spm_${network}_${timestamp}_${random}`;
    };

    const emitChanges = () => {
      const formattedValue = socialNetworks.value.map((item) => ({
        key: item.key,
        network: item.network,
        label: `templateBuilder.fields.${item.network}`,
        value: item.url,
        customIcon: item.customIcon,
        // Propriétés du séparateur (espacement uniquement)
        separatorPadding: item.separatorPadding,
      }));

      property.value.value = formattedValue;
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const handleAddNetwork = () => {
      const newNetwork: SocialNetworkItem = {
        key: generateUniqueKey('custom'),
        network: 'facebook',
        url: '',
        customIcon: '',
        separatorPadding: '5px',
        separatorPaddingValue: 5,
      };
      socialNetworks.value.push(newNetwork);
      emitChanges();
    };

    const handleRemoveNetwork = (index: number) => {
      socialNetworks.value.splice(index, 1);
      emitChanges();
    };

    const handleNetworkTypeChange = (index: number, event: any) => {
      const newNetwork = event.value;
      socialNetworks.value[index].network = newNetwork;
      socialNetworks.value[index].key = generateUniqueKey(newNetwork);
      if (newNetwork !== 'custom') {
        socialNetworks.value[index].customIcon = '';
      }
      emitChanges();
    };

    const handleUrlChange = (index: number) => {
      emitChanges();
    };

    const handleOrderChange = () => {
      emitChanges();
    };

    // Fonctions pour le séparateur
    const handleSeparatorPaddingInput = (index: number, value: number) => {
      socialNetworks.value[index].separatorPaddingValue = value;
      socialNetworks.value[index].separatorPadding = `${value}px`;
      emitChanges();
    };

    const openFileManager = (index: number) => {
      currentEditIndex.value = index;
      displayFileManager.value = true;
    };

    const onChooseCustomIcon = (file: File) => {
      if (currentEditIndex.value >= 0 && currentEditIndex.value < socialNetworks.value.length) {
        socialNetworks.value[currentEditIndex.value].customIcon = file.url;
        emitChanges();
      }
      displayFileManager.value = false;
      currentEditIndex.value = -1;
    };

    const handleRemoveCustomIcon = (index: number) => {
      socialNetworks.value[index].customIcon = '';
      emitChanges();
    };

    const onDragOver = (event: DragEvent, index: number) => {
      dragOverIndex.value = index;
    };

    const onDragLeave = () => {
      dragOverIndex.value = -1;
    };

    const convertFileToBase64 = (file: globalThis.File): Promise<string> => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        resolve(e.target.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });

    const uploadFile = async (file: globalThis.File, index: number) => {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        return;
      }

      uploadingIndex.value = index;
      try {
        const base64Data = await convertFileToBase64(file);
        const uploadResult = await createNewFileInMedia([base64Data], idShop, '');

        if (uploadResult?.data && Array.isArray(uploadResult.data) && uploadResult.data.length > 0) {
          const uploadedFile = uploadResult.data[0];
          const imageUrl = `https://media.shopimind.io/clients/${uploadedFile.path}`;
          socialNetworks.value[index].customIcon = imageUrl;
          emitChanges();
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        uploadingIndex.value = -1;
      }
    };

    const onDrop = (event: DragEvent, index: number) => {
      dragOverIndex.value = -1;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        uploadFile(file, index);
      }
    };

    watch(() => props.parserValues.properties[0], () => {
      property.value = props.parserValues.properties[0];
      initializeSocialNetworks();
      // Rafraîchir le style d'icône depuis le template
      iconStyle.value = getIconStyleFromTemplate();
    }, { immediate: true, deep: true });

    return {
      t,
      socialNetworks,
      availableNetworks,
      displayFileManager,
      getNetworkLabel,
      getNetworkDisplayLabel,
      getNetworkIconUrl,
      handleAddNetwork,
      handleRemoveNetwork,
      handleNetworkTypeChange,
      handleUrlChange,
      handleOrderChange,
      openFileManager,
      onChooseCustomIcon,
      handleRemoveCustomIcon,
      onDragOver,
      onDragLeave,
      onDrop,
      dragOverIndex,
      uploadingIndex,
      iconStyleKey,
      // Séparateur
      handleSeparatorPaddingInput,
    };
  },
});
</script>

<style scoped lang="scss">
.social-network-input-group {
  .elements-configuration {
    padding: 15px;
    border: solid 1px $solitude;
    border-radius: 3px;
  }

  .social-network-item {
    background: $alice-blue;
    border: 1px solid $solitude;
    border-radius: 4px;
    padding: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .social-network-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid $solitude;

    .drag-icon {
      color: $heather;
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }

    .network-label {
      flex: 1;
      font-weight: 500;
    }

    .delete-button {
      padding: 4px 8px;
    }
  }

  .social-network-content {
    .network-selector {
      margin-bottom: 12px;

      :deep(.p-dropdown-label) {
        padding: 0.4rem 0.5rem;
        display: flex;
        align-items: center;
      }
    }

    .network-option {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 4px 0;

      .network-icon {
        width: 24px;
        height: 24px;
        object-fit: contain;
        flex-shrink: 0;
        border-radius: 4px;
      }

      .network-icon-placeholder {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $heather;
        flex-shrink: 0;
      }

      span {
        line-height: 24px;
      }
    }

    .url-field {
      label {
        font-size: 0.9rem;
        color: $shuttle-grey;
      }
    }

    .custom-icon-section {
      label {
        font-size: 0.9rem;
        color: $shuttle-grey;
      }

      .custom-icon-preview {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: $white;
        border: 1px solid $solitude;
        border-radius: 4px;

        .preview-image {
          width: 48px;
          height: 48px;
          object-fit: contain;
        }

        .preview-actions {
          display: flex;
          gap: 4px;
        }
      }

      .custom-icon-upload {
        cursor: pointer;

        &.drag-over .upload-zone {
          border-color: $brand-color-primary;
          background: $extra-light-grey;
        }

        .upload-zone {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 24px;
          background: $white;
          border: 2px dashed $solitude;
          border-radius: 4px;
          text-align: center;
          transition: border-color 0.2s, background 0.2s;

          &:hover {
            border-color: $brand-color-primary;
          }

          .upload-icon {
            font-size: 2rem;
            color: $heather;
            margin-bottom: 8px;
          }

          .upload-text {
            margin: 0 0 4px 0;
            font-size: 0.9rem;
            color: $shuttle-grey;
          }

          .upload-hint {
            margin: 0;
            font-size: 0.8rem;
            color: $heather;
          }
        }
      }
    }
  }

  .add-network-section {
    margin-top: 16px;
  }

  .separator-fields {
    .separator-field {
      label {
        font-size: 0.9rem;
        color: $shuttle-grey;
      }
    }
  }
}
</style>
